import type {
  AdminPaymentRecord,
  PaymentExportParams,
  PaymentListParams,
  PaymentListResult,
  PaymentsSummary,
} from '@/types/payment.types';
import { cleanParams } from '@/utils/params.util';

import { apiClient } from './api.service';

/** Recovers the server's error message from an envelope that arrived as a Blob. */
const blobErrorToError = async (error: unknown): Promise<Error> => {
  const body = (error as { response?: { data?: unknown } })?.response?.data;
  if (body instanceof Blob) {
    try {
      const parsed = JSON.parse(await body.text()) as { error?: { message?: string } };
      if (parsed.error?.message) return new Error(parsed.error.message);
    } catch {
      // Not JSON — fall through to the generic message below.
    }
  }
  return error instanceof Error ? error : new Error('Export failed');
};

export const paymentsApi = {
  list: (params: PaymentListParams): Promise<PaymentListResult> =>
    apiClient
      .get<PaymentListResult>('/admin/payments', {
        params: cleanParams(params as unknown as Record<string, unknown>),
      })
      .then((r) => r.data),

  /**
   * Downloads the reconciliation workbook. `responseType: 'blob'` means the terminal error
   * interceptor cannot read the server's JSON envelope on failure (it arrives as a Blob too),
   * so the real message is recovered here rather than surfacing as "Request failed".
   */
  exportXlsx: async (params: PaymentExportParams): Promise<Blob> => {
    try {
      const response = await apiClient.get<Blob>('/admin/payments/export', {
        params: cleanParams(params as unknown as Record<string, unknown>),
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw await blobErrorToError(error);
    }
  },

  summary: (): Promise<PaymentsSummary> =>
    apiClient.get<PaymentsSummary>('/admin/payments/summary').then((r) => r.data),

  get: (id: string): Promise<AdminPaymentRecord> =>
    apiClient.get<AdminPaymentRecord>(`/admin/payments/${id}`).then((r) => r.data),

  verify: (id: string): Promise<AdminPaymentRecord> =>
    apiClient.post<AdminPaymentRecord>(`/admin/payments/${id}/verify`, {}).then((r) => r.data),

  reject: (id: string, reason: string): Promise<AdminPaymentRecord> =>
    apiClient
      .post<AdminPaymentRecord>(`/admin/payments/${id}/reject`, { reason })
      .then((r) => r.data),

  settle: (id: string, adminRef: string): Promise<AdminPaymentRecord> =>
    apiClient
      .post<AdminPaymentRecord>(`/admin/payments/${id}/settle`, { adminRef })
      .then((r) => r.data),

  /**
   * Closes a refund-due record: either the admin has sent the money back, or the payment was
   * never verified and the bank shows nothing arrived.
   */
  closeRefund: (
    id: string,
    body: { outcome: 'refunded'; ref?: string } | { outcome: 'no_payment_received'; reason: string },
  ): Promise<AdminPaymentRecord> =>
    apiClient
      .post<AdminPaymentRecord>(`/admin/payments/${id}/close-refund`, body)
      .then((r) => r.data),

  /** Pushes a reminder to whichever party is missing payout details. */
  requestPayoutDetails: (id: string): Promise<null> =>
    apiClient
      .post<null>(`/admin/payments/${id}/request-payout-details`, {})
      .then((r) => r.data),

  refundDeposit: (id: string, ref?: string): Promise<AdminPaymentRecord> =>
    apiClient
      .post<AdminPaymentRecord>(`/admin/payments/${id}/refund-deposit`, ref ? { ref } : {})
      .then((r) => r.data),
};
